import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface StartChatSessionRequest {
  name: string;
  key: string;
  phone?: string;
  email?: string;
  language: string;
}

export interface StartChatSessionResponse {
  sessionId: number;
  success: boolean;
  message: string;
}

export interface SendMessageRequest {
  sessionId: number;
  message: string;
}

export interface SendMessageResponse {
  success: boolean;
  message: string;
}

export interface ChatMessageDto {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  sessionId: number;
}

export interface AccountOpeningRequest {
  fullName: string;
  phoneNumber: string;
  email: string;
  address: string;
  hasExperience: boolean;
  tradesWithOtherCompany: boolean;
  language: string;
}

export interface AccountOpeningResponse {
  success: boolean;
  message: string;
  ticketId?: number;
}

export interface StockRecommendationRequest {
  accountNumber: string;
  language: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private baseUrl = environment.apiUrl || 'https://www.pioneers-securities.com/webdataapi/api'; 
  private currentSessionId: number | null = null;
  private messagesSubject = new BehaviorSubject<ChatMessageDto[]>([]);
  public messages$ = this.messagesSubject.asObservable();

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) { }

  async startChatSession(request: StartChatSessionRequest): Promise<StartChatSessionResponse> {
    try {
      const response = await this.http.post<StartChatSessionResponse>(
        `${this.baseUrl}/chat/start-session`,
        request,
        this.httpOptions
      ).toPromise();

      if (response && response.success) {
        this.currentSessionId = response.sessionId;
      }

      return response || { sessionId: 0, success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Error starting chat session:', error);
      return { sessionId: 0, success: false, message: 'Failed to start session' };
    }
  }

  async sendMessage(message: string): Promise<SendMessageResponse> {
    if (!this.currentSessionId) {
      return { success: false, message: 'No active session' };
    }

    try {
      const request: SendMessageRequest = {
        sessionId: this.currentSessionId,
        message: message
      };

      const response = await this.http.post<SendMessageResponse>(
        `${this.baseUrl}/chat/send-message`,
        request,
        this.httpOptions
      ).toPromise();

      return response || { success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Error sending message:', error);
      return { success: false, message: 'Failed to send message' };
    }
  }

  async endSession(): Promise<boolean> {
    if (!this.currentSessionId) {
      return false;
    }

    try {
      const response = await this.http.post<boolean>(
        `${this.baseUrl}/chat/end-session/${this.currentSessionId}`,
        {},
        this.httpOptions
      ).toPromise();

      if (response) {
        this.currentSessionId = null;
        this.messagesSubject.next([]);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error ending session:', error);
      return false;
    }
  }

  async getSessionMessages(): Promise<ChatMessageDto[]> {
    if (!this.currentSessionId) {
      return [];
    }

    try {
      const response = await this.http.get<ChatMessageDto[]>(
        `${this.baseUrl}/chat/messages/${this.currentSessionId}`,
        this.httpOptions
      ).toPromise();

      return response || [];
    } catch (error) {
      console.error('Error getting session messages:', error);
      return [];
    }
  }

  async getQueuePosition(key: string): Promise<number> {
    try {
      const response = await this.http.get<number>(
        `${this.baseUrl}/chat/queue-position/${key}`,
        this.httpOptions
      ).toPromise();

      return response || -1;
    } catch (error) {
      console.error('Error getting queue position:', error);
      return -1;
    }
  }

  getCurrentSessionId(): number | null {
    return this.currentSessionId;
  }

  addMessage(message: ChatMessageDto): void {
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, message]);
  }

  clearMessages(): void {
    this.messagesSubject.next([]);
  }

  async createAccountOpeningTicket(request: AccountOpeningRequest): Promise<AccountOpeningResponse> {
    try {
      const response = await this.http.post<AccountOpeningResponse>(
        `${this.baseUrl}/chat/account-opening`,
        request,
        this.httpOptions
      ).toPromise();

      return response || { success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Error creating account opening ticket:', error);
      return { success: false, message: 'Failed to create account opening ticket' };
    }
  }

  async createStockRecommendationsTicket(request: StockRecommendationRequest): Promise<AccountOpeningResponse> {
    try {
      const response = await this.http.post<AccountOpeningResponse>(
        `${this.baseUrl}/chat/stock-recommendations`,
        request,
        this.httpOptions
      ).toPromise();

      return response || { success: false, message: 'Unknown error' };
    } catch (error) {
      console.error('Error creating stock recommendations ticket:', error);
      return { success: false, message: 'Failed to create stock recommendations ticket' };
    }
  }
} 